import { useContext, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { GoogleMapsContext } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

type Props = {
  lat: number
  lng: number
  radius: number
  marker?: google.maps.Marker
  setMarker: (newLatLng: google.maps.Marker) => void
  setShowHint: () => void
}

const DynamicMap = ({ lat, lng, radius, marker, setMarker, setShowHint }: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const confirmLocationDiv = useLazyRef(() => document.createElement('div'))
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const zoom = radius < 500 ? 17 : 14
  const { placesLibraryLoaded } = useContext(GoogleMapsContext)

  const circle = useUnsafeMemo(
    () =>
      new google.maps.Circle({
        strokeColor: '#96B2FF',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#96B2FF',
        fillOpacity: 0.35,
        map,
        center: { lat, lng },
        radius: radius,
      }),
    [marker]
  )

  const initMap = async () => {
    if (!ref.current) return
    const Map = google.maps.Map
    const googleMap = new Map(ref.current, {
      disableDefaultUI: true,
      zoom,
      center: {
        lat,
        lng,
      },
    })
    googleMap.controls[google.maps.ControlPosition.RIGHT_BOTTOM]?.push(confirmLocationDiv.current)

    setMap(googleMap)
    setMarker(
      new google.maps.Marker({
        position: { lat, lng },
        map,
        draggable: true,
      })
    )
    return googleMap
  }

  useEffect(() => {
    if (typeof google === 'undefined') return
    if (!map) initMap()
    map?.setCenter({ lat, lng })
  }, [lat, lng, placesLibraryLoaded])

  useUnsafeEffect(() => {
    if (!marker) return

    marker.setOptions({ position: { lat, lng }, map, draggable: true })
    google.maps.event.addListener(marker, 'dragend', () => {
      const newPosition = marker?.getPosition()
      if (!newPosition) return

      setShowHint()
      circle.setCenter(newPosition)
    })

    return () => {
      marker?.setMap(null)
    }
  }, [marker])

  return (
    <Box ref={ref} height="full" padding="s32" width="full" borderRadius={{ all: 'abs008' }}></Box>
  )
}

export default DynamicMap
